import { useContext, useEffect, useState } from "react"
import { Button } from "@components/common/button"
import { Popover, PopoverClose, PopoverContent, PopoverTrigger } from "@components/common/popover"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Filter, X } from "lucide-react"
import { Separator } from "@components/common/separator"
import useTranslator from "@pma/hooks/use-translator"
import { Calendar } from "@components/common/calendar"
import { he } from "date-fns/locale"
import { DateRange } from "react-day-picker"
import { format, startOfToday } from "date-fns"
import { GroupedTasksContext } from "./context/GroupedTasksProvider"
import { Switch } from "@pma/components/common/switch"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pma/components/common/select"
import { cn } from "@pma/utils/helpers"
interface FilterGroupsProps<TData> {
  clearFilter: boolean
}

export function FilterGroups<TData>({ clearFilter }: FilterGroupsProps<TData>) {
  const navigate = useNavigate()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const newParams = new URLSearchParams(searchParam)

  const isHebrew = locale === "he"
  const activeStatus = searchParam.get("status")
  const activeUser = searchParam.get("user")
  const activeStartDate = searchParam.get("from")
  const activeEndDate = searchParam.get("to")
  const runningStatus = searchParam.get("tracker")
  const showDeletedTasks = searchParam.get("showDeletedTasks")
  const activePriority = searchParam.get("priority")

  const { orgUsers, taskStatus, setPagination, taskPrioritiesList } =
    useContext(GroupedTasksContext)

  const [open, setOpen] = useState(false)
  const [oneTimeSetValue, setOneTimeSetValue] = useState<boolean>(false)
  const [selectedUser, setSelectedUser] = useState<any>(searchParam.get("user") || "")
  const [selectedStatus, setSelectedStatus] = useState<any>(searchParam.get("status") || "")
  const [date, setDate] = useState<DateRange | undefined>({ from: undefined, to: undefined })
  const [month, setMonth] = useState(date?.from || undefined)
  const [selectedPriority, setSelectedPriority] = useState<any>(searchParam.get("priority") || "")

  useEffect(() => {
    if (taskStatus != undefined && oneTimeSetValue == false) {
      setOneTimeSetValue(true)
      setSelectedStatus(activeStatus ? activeStatus : "")
    }
  }, [taskStatus])

  useEffect(() => {
    if (orgUsers?.items) {
      setSelectedUser(activeUser ? activeUser : "")
    }
  }, [orgUsers])

  useEffect(() => {
    if (taskPrioritiesList) setSelectedPriority(activePriority ? activePriority : "")
  }, [taskPrioritiesList])

  const selectStatus = () => {
    if (selectedStatus !== "") newParams.set("status", selectedStatus)
    else newParams.delete("status")

    setPagination({ pageIndex: 1, pageSize: 50 })
    navigate(`?${newParams.toString()}`)
  }

  const selectUser = (user: string) => {
    if (user) newParams.set("user", user)
    else newParams.delete("user")

    setPagination({ pageIndex: 1, pageSize: 50 })
    navigate(`?${newParams.toString()}`)
  }

  const setTracker = (tracker: string) => {
    if (tracker) newParams.set("tracker", tracker)
    else newParams.delete("tracker")

    setPagination({ pageIndex: 1, pageSize: 50 })
    navigate(`?${newParams.toString()}`)
  }

  const setShowDeletedTasks = (showDeletedTasks: string) => {
    if (showDeletedTasks) newParams.set("showDeletedTasks", showDeletedTasks)
    else newParams.delete("showDeletedTasks")

    setPagination({ pageIndex: 1, pageSize: 50 })
    navigate(`?${newParams.toString()}`)
  }

  useEffect(() => {
    return () => {
      newParams.delete("user")
      newParams.delete("status")
      newParams.delete("from")
      newParams.delete("to")
      newParams.delete("priority")
    }
  }, [])

  useEffect(() => {
    selectUser(selectedUser)
  }, [selectedUser])

  useEffect(() => {
    selectStatus()
  }, [selectedStatus])

  const selectPriority = () => {
    if (selectedPriority !== "") newParams.set("priority", selectedPriority)
    else newParams.delete("priority")

    setPagination({ pageIndex: 1, pageSize: 50 })
    navigate(`?${newParams.toString()}`)
  }

  useEffect(() => {
    selectPriority()
  }, [selectedPriority])

  useEffect(() => {
    if (clearFilter) {
      setDate({ from: undefined, to: undefined })
      setSelectedUser("")
      setSelectedStatus("")
      setSelectedPriority("")
    }
  }, [clearFilter])

  useEffect(() => {
    if (activeStartDate && activeEndDate) {
      setDate({
        from: new Date(Number(activeStartDate)),
        to: new Date(Number(activeEndDate)),
      })
    }
  }, [activeStartDate, activeEndDate])

  const handleSelect = (range: any) => {
    setDate(range)
    if (range?.from) setMonth(range.from) // 👈 Update month view on select
  }

  const handelDateSave = () => {
    if (date?.from && date.to) {
      newParams.set("from", String(date?.from.getTime()))
      newParams.set("to", String(date?.to?.getTime()))
      setPagination({ pageIndex: 1, pageSize: 50 })
      navigate(`?${newParams.toString()}`)
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant='outline'
          className='ml-auto w-full md:w-[100px]'
          size={"sm"}
          leftSection={<Filter size={14} />}
        >
          {translate("Filter")}
        </Button>
      </PopoverTrigger>
      <PopoverContent className={cn("w-80", isHebrew ? "rtl" : "ltr")} side='bottom'>
        <div className='grid gap-4'>
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Date Range")}</h4>
            <div className='gapy-y-3 flex flex-wrap items-center gap-2'>
              <Popover>
                <div className='relative w-full'>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        "w-full rounded-md border border-gray-300 px-3 py-2 text-left text-xs focus:outline-none focus:ring-1 focus:ring-ring",
                        isHebrew ? "pl-8 text-right" : "pr-8 text-left",
                      )}
                    >
                      {date?.from != undefined ? (
                        date?.from ? (
                          date?.to ? (
                            <>
                              {format(date?.from, "LLL dd, y", {
                                locale: isHebrew ? he : undefined,
                              })}{" "}
                              -{" "}
                              {format(date?.to, "LLL dd, y", {
                                locale: isHebrew ? he : undefined,
                              })}
                            </>
                          ) : (
                            format(date?.from, "LLL dd, y", {
                              locale: isHebrew ? he : undefined,
                            })
                          )
                        ) : activeStartDate ? (
                          <>
                            {format(new Date(activeStartDate), "LLL dd, y", {
                              locale: isHebrew ? he : undefined,
                            })}{" "}
                            -{" "}
                            {format(new Date(activeEndDate!), "LLL dd, y", {
                              locale: isHebrew ? he : undefined,
                            })}
                          </>
                        ) : (
                          format(new Date(activeStartDate!), "LLL dd, y", {
                            locale: isHebrew ? he : undefined,
                          })
                        )
                      ) : (
                        <span className='text-sm'>{translate("Select date to filter")}</span>
                      )}
                    </button>
                  </PopoverTrigger>

                  {date?.from && (
                    <button
                      type='button'
                      onClick={(e) => {
                        e.stopPropagation() // prevent Popover from opening
                        setDate({ from: undefined, to: undefined })
                      }}
                      className={cn(
                        "absolute top-1/2 -translate-y-1/2 transform text-gray-400 hover:text-gray-600",
                        isHebrew ? "left-2" : "right-2",
                      )}
                    >
                      &times;
                    </button>
                  )}
                </div>

                <PopoverContent className='mt-2 w-fit rounded-md border border-gray-200 bg-white p-2 shadow-lg'>
                  <Calendar
                    initialFocus
                    mode='range'
                    locale={isHebrew ? he : undefined}
                    selected={date}
                    onSelect={handleSelect}
                    numberOfMonths={2}
                    disabled={[{ after: startOfToday() }]}
                    month={month} // 👈 Controlled month state
                    onMonthChange={setMonth} // 👈 Allow navigation with buttons
                  />
                  <div className='flex w-full justify-center p-2'>
                    <PopoverClose>
                      <Button size={"sm"} className='w-[200px]' onClick={() => handelDateSave()}>
                        {translate("Save")}
                      </Button>
                    </PopoverClose>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
          <Separator />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Status")}</h4>
            <div className='flex flex-wrap items-center gap-2'>
              <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                <div className='relative w-full'>
                  <SelectTrigger
                    className={cn(
                      "w-full rounded-lg sm:ml-auto",
                      selectedStatus != "" ? "pr-10" : "",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    <SelectValue placeholder={translate("Select Status")} />
                  </SelectTrigger>
                  {selectedStatus && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent opening the dropdown
                        setSelectedStatus("") // Clear selection
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      aria-label='Clear selection'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {taskStatus?.map((status, index) => (
                    <SelectItem value={`${status?.id}`} className='rounded-lg' key={index}>
                      {status?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Assigned To")}</h4>
            <div className='gapy-y-3 flex flex-wrap items-center gap-2'>
              <Select value={selectedUser} onValueChange={setSelectedUser}>
                <div className='relative w-full'>
                  <SelectTrigger
                    className={cn(
                      "w-full rounded-lg sm:ml-auto",
                      selectedUser != "" ? "pr-10" : "",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    <SelectValue placeholder={translate("Select Active user")} />
                  </SelectTrigger>
                  {selectedUser && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent opening the dropdown
                        setSelectedUser("") // Clear selection
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      aria-label='Clear selection'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {orgUsers?.items?.map((user, index) => (
                    <SelectItem value={`${user?.id}`} className='rounded-lg' key={index}>
                      {user?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Priority")}</h4>
            <div className='gapy-y-3 flex flex-wrap items-center gap-2'>
              <Select value={selectedPriority} onValueChange={setSelectedPriority}>
                <div className='relative w-full'>
                  <SelectTrigger
                    className={cn(
                      "w-full rounded-lg sm:ml-auto",
                      selectedPriority != "" ? "pr-10" : "",
                      isHebrew ? "rtl" : "ltr",
                    )}
                  >
                    <SelectValue placeholder={translate("Select Priority")} />
                  </SelectTrigger>
                  {selectedPriority && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent opening the dropdown
                        setSelectedPriority("") // Clear selection
                      }}
                      className='absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600'
                      aria-label='Clear selection'
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
                <SelectContent className={cn("rounded-xl", isHebrew ? "rtl" : "ltr")}>
                  {taskPrioritiesList?.map((priority, index) => (
                    <SelectItem value={`${priority?.id}`} className='rounded-lg' key={index}>
                      {priority?.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Separator />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Time Tracker")}</h4>
            <div className='flex flex-wrap items-center gap-2'>
              <Switch
                checked={!!runningStatus}
                className='h-6 w-11'
                onCheckedChange={(checked) => {
                  return !!checked ? setTracker("Running") : setTracker("")
                }}
              />
            </div>
          </div>
          <Separator />
          <div className='space-y-2'>
            <h4 className='text-sm font-semibold'>{translate("Show Deleted Tasks")}</h4>
            <div className='flex flex-wrap items-center gap-2'>
              <Switch
                checked={!!showDeletedTasks}
                className='h-6 w-11'
                onCheckedChange={(checked) => {
                  return !!checked ? setShowDeletedTasks("true") : setShowDeletedTasks("")
                }}
              />
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
